import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Canonical SECONDARY: white fill, 2px solid black border, black text, long
        // shadow (same language as the hero / knowledge-panel `long-outline`).
        outline:
          "bg-background text-gray-900 border-2 border-gray-900 shadow-long transition-all hover:bg-gray-50 hover:shadow-long-lift hover:-translate-x-0.5 hover:-translate-y-0.5",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Canonical PRIMARY: solid violet CTA with the hard-offset long shadow that
        // grows + lifts on hover (same language as the hero / `long-brand`).
        brand:
          "bg-cta text-white shadow-long-brand transition-all hover:bg-cta-hover hover:shadow-long-brand-lift hover:-translate-x-0.5 hover:-translate-y-0.5",
        "brand-light":
          "bg-brand-gradient-light text-gray-900 border border-white/50 hover:opacity-90",
        // Heavier-border secondary CTA (e.g. header "View Profile"). Sanctioned so call
        // sites stop hand-rolling border-2/bg-white overrides on top of `outline`.
        "outline-strong":
          "border-2 border-gray-900 bg-background text-foreground hover:bg-gray-50",
        // Outline that inverts to solid on hover (marketing pages: press kit, careers CTAs).
        "outline-invert":
          "border border-gray-900 text-gray-900 bg-transparent hover:bg-gray-900 hover:text-white",
        // Sanctioned "affirmative action" blue (save/confirm in admin surfaces), driven by
        // the --action-blue token. Replaces one-off bg-[#hex] / bg-action-blue overrides.
        action: "bg-action-blue text-white hover:bg-action-blue/90",
        // Outlined destructive (reject/delete without full-solid alarm). Recurring admin
        // pattern — sanctioned so it stops being hand-rolled per call site.
        "destructive-outline":
          "border border-red-200 text-red-600 hover:bg-red-50",
        // Light-fill destructive (remove/detach actions that shouldn't read as alarming
        // as the solid `destructive` variant).
        "destructive-subtle": "bg-red-50 text-red-600 hover:bg-red-100",
        // Solid approve/confirm action (admin moderation flows).
        success: "bg-green-600 text-white hover:bg-green-700",
        // ── Knowledge-panel (long-shadow) CTAs — the /pro instructor page.
        // Violet primary: hard offset shadow that grows + lifts on hover.
        "long-brand":
          "bg-cta text-white shadow-long-brand transition-all hover:bg-cta-hover hover:shadow-long-brand-lift hover:-translate-x-0.5 hover:-translate-y-0.5",
        // Black-outline secondary in the same card language (the instructor Book buttons).
        "long-outline":
          "bg-background text-gray-900 border-2 border-gray-900 shadow-long transition-all hover:bg-gray-50 hover:shadow-long-lift hover:-translate-x-0.5 hover:-translate-y-0.5",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Compact icon button (avatar triggers, collapsed nav toggles).
        "icon-sm": "h-8 w-8",
      },
      shape: {
        default: "",
        // Full pill radius for the brand CTA + panel Book buttons (Brandfetch-style).
        // Use this instead of a `rounded-full` className override.
        pill: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const resolved = cn(buttonVariants({ variant, size, shape, className }))
    // [debug:button] TEMP — validating button-variant drift (hero vs header).
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.log("[debug:button] variant=", variant ?? "(default)", "| shape=", shape ?? "(none)", "| hasShadowLong=", resolved.includes("shadow-long"), "| classes=", resolved)
    }
    return (
      <Comp
        className={resolved}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
